import { InfoCircleTwoTone } from "@ant-design/icons";
import { assertNever } from "@p0security/frontend-core";
import { SharedNodeTitles } from "@p0security/shared/assessment/constants";
import { Node } from "@p0security/shared/graph/types";
import { Provider } from "@p0security/shared/types/assessment";
import { IdentityCredentials } from "@p0security/shared/types/assessment/data/identity";
import {
  AssessmentNodes,
  IdentityType,
} from "@p0security/shared/types/assessment/data/index";
import { Flex, Grid, List, Segmented, Typography } from "@p0security/ui";
import { GraphTooltip } from "components/GraphTooltip";
import { useMemo, useState } from "react";

import {
  credentialsByStaleness,
  credentialsByUsage,
} from "../node/identity/grouping";
import { identityTypesWithCredentials } from "../node/identity/shared";
import { DisplayList } from "../node/shared";
import { CountAggregate } from "./Aggregate";
import { KeyLink } from "./KeyLink";
import { NodeLink } from "./NodeLink";
import { HasAddTerm, ShowHideTerm } from "./ShowHide";

export const identityInfoFromKey = (
  //TODO: Make more general
  credentialKey: string,
  source: Provider
) => {
  let projectId = "";
  let serviceAccountId = "";
  let keyId = "";
  let _itemType = "";
  let _ = "";
  if (source === "gcp") {
    [projectId, _, serviceAccountId, _itemType, keyId] = credentialKey
      .split("/")
      .slice(4);
  } else if (source === "aws") {
    [projectId, serviceAccountId, keyId] = credentialKey.split("/");
  }
  return {
    projectId,
    serviceAccountId,
    keyId,
  };
};

export const UsedCredentialAggregate = ({
  credentials,
  onAddTerm,
  terms,
}: {
  credentials: Record<string, IdentityCredentials>;
} & HasAddTerm) => {
  return CountAggregate({
    getSearchTerm: (val) => `credential:last90:"${val}"`,
    inputMap: credentials,
    typeOptions: ["used", "unused"],
    onAddTerm,
    terms,
    termName: "credentials",
  });
};

export const StaleCredentialAggregate = ({
  credentials,
  onAddTerm,
  terms,
}: {
  credentials: Record<string, IdentityCredentials>;
} & HasAddTerm) => {
  return CountAggregate({
    getSearchTerm: (val) =>
      val === "stale" ? "credential:stale90:true" : "credential:stale90:false",
    inputMap: credentials,
    typeOptions: ["stale", "fresh"],
    onAddTerm,
    terms,
    termName: "credentials",
  });
};

export const credentialTitle = (node: Node<AssessmentNodes, "credential">) => {
  const { provider, type } = node.data;
  if (provider === "gcp") {
    if (type === "key") {
      return "Service-account key";
    }
    if (type === "short-lived") {
      return "Service-account impersonation";
    }
  }
  if (provider === "aws" && type === "key") {
    return "IAM user access key";
  }
  return SharedNodeTitles.credential;
};

export const CredentialDisplay: React.FC<
  Partial<HasAddTerm> & {
    credential: AssessmentNodes["credential"];
    id: string;
  }
> = ({ credential, id, terms, onAddTerm }) => {
  if (!id) return null;
  const { projectId, serviceAccountId, keyId } = identityInfoFromKey(
    id,
    credential.provider
  );
  const keyComponent = (
    <>
      Key&nbsp;
      <KeyLink
        projectId={projectId}
        serviceAccountId={serviceAccountId}
        keyId={keyId}
        source={credential.provider}
      />
    </>
  );
  return (
    <GraphTooltip
      title={
        <>
          {credential.type === "key" && credential.status === "disabled" ? (
            "This key has been disabled"
          ) : credential.type === "short-lived" ? (
            <>
              <Typography.Paragraph>
                Short-lived credentials represent all ephemeral login
                methods.{" "}
              </Typography.Paragraph>
              <Typography.Paragraph>
                {" "}
                In GCP, this includes JWT tokens generated using{" "}
                <Typography.Link
                  href="https://cloud.google.com/iam/docs/create-short-lived-credentials-direct"
                  target="_blank"
                  rel="noopener"
                >
                  direct token creation
                </Typography.Link>
                , service-account impersonation, and service-account usage from
                Google-managed services.{" "}
              </Typography.Paragraph>
              <Typography.Paragraph>
                In AWS, this includes temporary credentials generated via{" "}
                <Typography.Link
                  href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_credentials_temp.html"
                  target="_blank"
                  rel="noopener"
                >
                  AWS STS
                </Typography.Link>
                , such as through sts:AssumeRole.
              </Typography.Paragraph>
              <Typography.Paragraph>
                In Azure, this includes OAuth tokens issued by{" "}
                <Typography.Link
                  href="https://learn.microsoft.com/en-us/entra/identity-platform/access-tokens"
                  target="_blank"
                  rel="noopener"
                >
                  Microsoft Entra ID
                </Typography.Link>{" "}
                when users and service principals sign in.
              </Typography.Paragraph>
            </>
          ) : credential.type === "key" ? (
            "Static credential"
          ) : credential.type === "federated" ? (
            "Federated access"
          ) : credential.type === "password" ? (
            "User password"
          ) : (
            assertNever(credential.type)
          )}
          {terms !== undefined && onAddTerm && (
            <>
              <ShowHideTerm
                term={`credential=type:"${credential.type}"`}
                name="credential types"
                terms={terms}
                onAddTerm={onAddTerm}
              />
            </>
          )}
        </>
      }
    >
      {credential.type === "key" ? (
        keyComponent
      ) : credential.type === "federated" ? (
        <>Federated login</>
      ) : credential.type === "short-lived" ? (
        <>Short-lived credentials</>
      ) : credential.type === "password" ? (
        <>User Password</>
      ) : (
        assertNever(credential.type)
      )}
      {(credential.type === "short-lived" ||
        (credential.type === "key" && credential.status === "disabled")) && (
        <>
          &nbsp;
          <InfoCircleTwoTone />
        </>
      )}
    </GraphTooltip>
  );
};

export const CredentialList: React.FC<{
  credentials: IdentityCredentials;
  identityType: IdentityType;
}> = ({ credentials, identityType }) => {
  const [usedSelected, setUsedSelected] = useState<number | string>("unused");

  const credentialsUsage = credentialsByUsage(credentials);
  const credentialsStaleness = credentialsByStaleness(credentials);
  const allCredentials: IdentityCredentials = useMemo(
    () => [
      ...(credentialsUsage["used"] ?? []),
      ...(credentialsUsage["unused"] ?? []),
    ],
    [credentialsUsage]
  );
  const usedOptions = useMemo(
    () => [
      {
        label: `Unused (${credentialsUsage.unused?.length ?? 0})`,
        value: "unused",
      } as const,
      {
        label: `Used (${credentialsUsage.used?.length ?? 0})`,
        value: "used",
      } as const,

      {
        label: `All (${credentials.length})`,
        value: "all",
      } as const,
    ],
    [credentialsUsage, credentials]
  );

  const [staleSelected, setStaleSelected] = useState<number | string>("all");

  const used: string[] = useMemo(() => {
    const selected =
      usedSelected === "all"
        ? allCredentials
        : (credentialsUsage[usedSelected as keyof typeof credentialsUsage] ??
          []);
    return selected.map((n) => n.id);
  }, [allCredentials, credentialsUsage, usedSelected]);
  const stale = useMemo(() => {
    const selected =
      staleSelected === "all"
        ? allCredentials
        : (credentialsStaleness[
            staleSelected as keyof typeof credentialsStaleness
          ] ?? []);
    return selected.map((n) => n.id);
  }, [allCredentials, credentialsStaleness, staleSelected]);

  const staleOptions = useMemo(() => {
    const credCount = (type: "fresh" | "stale") =>
      used.filter(
        (x) => !!credentialsStaleness?.[type]?.find((n) => n.id === x)
      ).length;

    return [
      {
        label: `Stale (${credCount("stale")})`,
        value: "stale",
      } as const,
      {
        label: `Fresh (${credCount("fresh")})`,
        value: "fresh",
      } as const,
      {
        label: `All (${used.length})`,
        value: "all",
      } as const,
    ];
  }, [used, credentialsStaleness]);

  const shown = useMemo(() => {
    return stale.filter((x) => !!used.find((n) => n === x));
  }, [stale, used]);

  const { xxl } = Grid.useBreakpoint();

  return identityTypesWithCredentials.includes(identityType) ? (
    <Flex className="w-full" vertical gap="0.7em" align="flex-start">
      <Flex gap=".5em" wrap={xxl ? "nowrap" : "wrap"}>
        <GraphTooltip title="Unused credentials have not been used in the past 90 days">
          <div style={{ width: xxl ? "unset" : "25em" }}>
            <Segmented
              options={usedOptions}
              value={usedSelected}
              onChange={setUsedSelected}
              block={!xxl}
            />
          </div>
        </GraphTooltip>

        {identityType === "service-account" && (
          <GraphTooltip title="Stale credentials have not been rotated in the past 90 days">
            <div style={{ width: xxl ? "unset" : "25em" }}>
              <Segmented
                options={staleOptions}
                value={staleSelected}
                onChange={setStaleSelected}
                block={!xxl}
              />
            </div>
          </GraphTooltip>
        )}
      </Flex>
      <CredentialInnerList shown={shown} credentials={credentials} />
    </Flex>
  ) : null;
};

const CredentialInnerList: React.FC<{
  shown: string[];
  credentials: IdentityCredentials;
}> = ({ shown, credentials }) => {
  const items = useMemo(() => {
    const included = new Set(shown);
    return credentials
      .filter((c) => c && included.has(c.id))
      .map(({ id, ...c }) => (
        <List.Item key={id}>
          <NodeLink
            node={{
              type: "credential" as const,
              key: id,
            }}
          />
          &nbsp;
          <CredentialDisplay credential={c} id={id} />
        </List.Item>
      ));
  }, [credentials, shown]);

  return <DisplayList className="w-full">{items}</DisplayList>;
};
